import { useEffect, useRef } from 'react';
import { isTabKeepAliveEnabled } from '../config/tabKeepAlive';
import {
  acquirePaneHttpAttribution,
  runWithPaneHttpContext,
} from '../routing/keepAliveNetworkGate';
import { useTabPaneContext } from '../routing/TabPaneContext';
import { useIsActiveView } from '../routing/ViewActivityContext';

const serializeDeps = (deps: readonly unknown[]): string => JSON.stringify(deps);

/**
 * Indica se o pane keep-alive atual está visível (aba ativa).
 * Sem keep-alive, sempre true.
 */
export const useIsActivePane = (): boolean => {
  const paneContext = useTabPaneContext();
  if (!isTabKeepAliveEnabled() || !paneContext) {
    return true;
  }
  return paneContext.isPaneActive;
};

/**
 * useEffect para carga de dados compatível com keep-alive:
 * - não refaz request ao voltar para uma aba já carregada (mesmos deps)
 * - não executa enquanto o pane está oculto
 * - recarrega quando os deps mudam (filtros, paginação, etc.)
 * - enabled permite pausar o efeito para uma sub-aba ainda não selecionada
 */
export function useKeepAliveDataEffect(
  effect: () => void | (() => void),
  deps: React.DependencyList,
  enabled = true,
): void {
  const paneContext = useTabPaneContext();
  const isActiveView = useIsActiveView();
  const isKeepAlive = isTabKeepAliveEnabled();
  const committedKeyRef = useRef<string | null>(null);
  const effectRef = useRef(effect);
  effectRef.current = effect;

  const depsKey = serializeDeps(deps);
  const isPaneActive = paneContext?.isPaneActive ?? true;
  const isEffectActive = enabled && isPaneActive && isActiveView;

  useEffect(() => {
    if (!isEffectActive) {
      return;
    }

    if (committedKeyRef.current === depsKey) {
      return;
    }

    committedKeyRef.current = depsKey;
    const attributedPanePath = isKeepAlive && paneContext
      ? paneContext.panePath
      : null;
    const releaseAttribution = attributedPanePath
      ? acquirePaneHttpAttribution(attributedPanePath)
      : () => undefined;

    let effectCleanup: void | (() => void);
    runWithPaneHttpContext(attributedPanePath, () => {
      effectCleanup = effectRef.current();
    });

    return () => {
      releaseAttribution();
      const cleanupFn = effectCleanup;
      if (typeof cleanupFn === 'function') {
        cleanupFn();
        // Efeitos async cancelados (StrictMode/deps) precisam poder rodar de novo.
        committedKeyRef.current = null;
      }
    };
  }, [isKeepAlive, isEffectActive, paneContext, depsKey]);
}
